"use client";
import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "motion/react";
import { useTranslations } from "next-intl";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
} as const;

type ServiceItem = { number: string; title: string; tags: string[]; description: string };

const ServiceRow = ({ service }: { service: ServiceItem }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      variants={itemVariants}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative border-b border-white/[0.07] cursor-default"
    >
      {/* Hover background */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-theme/[0.04] to-transparent pointer-events-none"
        style={{ opacity: 0 }}
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      />

      <div className="relative z-10 py-[22px] md:py-[26px] lg:py-[28px] px-[16px] md:px-[20px] lg:px-[24px] grid grid-cols-[40px_1fr] md:grid-cols-[50px_1fr_auto] lg:grid-cols-[60px_1fr_auto] gap-x-[20px] md:gap-x-[28px] lg:gap-x-[32px] items-start md:items-center">
        {/* Number */}
        <span className="text-[12px] font-medium text-theme/40 pt-[4px] lg:pt-0 tabular-nums transition-colors duration-200 group-hover:text-theme/70">
          {service.number}
        </span>

        {/* Title + description */}
        <div>
          <h3 className="text-[20px] md:text-[24px] lg:text-[28px] font-semibold leading-[1.2] transition-colors duration-200 group-hover:text-[#6b52b5] mb-[0px]">
            {service.title}
          </h3>

          {/* Mobile tags - show below title */}
          <div className="flex md:hidden flex-wrap gap-[6px] mt-[8px]">
            {service.tags.map((tag) => (
              <span key={tag} className="px-[10px] py-[4px] rounded-full text-[11px] font-light border border-white/10 text-foreground/40">
                {tag}
              </span>
            ))}
          </div>

          {/* Mobile: always show description */}
          <p className="md:hidden text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[600px] mt-[10px]">
            {service.description}
          </p>

          {/* Desktop: show on hover */}
          <AnimatePresence>
            {hovered && (
              <motion.p
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: "auto", marginTop: 10 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="hidden md:block text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[600px] overflow-hidden"
              >
                {service.description}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop only tags column */}
        <div className="hidden md:flex flex-wrap gap-[6px] justify-end">
          {service.tags.map((tag) => (
            <span
              key={tag}
              className="px-[10px] py-[4px] rounded-full text-[11px] font-light border border-white/10 text-foreground/40 group-hover:border-theme/25 group-hover:text-theme/60 transition-colors duration-200"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ServicesList = () => {
  const t = useTranslations("services");
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });
  const items = t.raw("list.items") as { title: string; tags: string; description: string }[];
  const services: ServiceItem[] = items.map((item, i) => ({
    number: String(i + 1).padStart(2, "0"),
    title: item.title,
    tags: item.tags.split(", "),
    description: item.description,
  }));

  return (
    <section
      id="services"
      ref={sectionRef}
      className="section pt-[20px] lg:pt-[40px] pb-[60px] lg:pb-[140px] relative"
    >
      <div
        className="absolute left-[0%] top-[10%] w-[600px] h-[600px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.07)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
        />

      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div className="text-center mb-[50px] lg:mb-[60px]" variants={itemVariants}>
            <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
              {t("list.sectionTitle")}
            </h2>
            <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
              {t("list.sectionDescription")}
            </p>
          </motion.div>

          {/* Top divider */}
          <div className="max-w-[900px] mx-auto border-t border-white/[0.07]" />

          <div className="max-w-[900px] mx-auto">
            {services.map((service, i) => (
              <ServiceRow key={i} service={service} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesList;
